import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import {
  Dropdown,
  type DropdownOption,
} from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import type { SalaryAdjustmentAddFormState } from "./adjustSalaryModal.hook";
import { salaryAdjustmentPresentation } from "./adjustSalaryModal.presentation";
import { AdjustmentForm, AmountSign } from "./adjustSalaryModal.styled";

interface Props {
  readonly addFormState: SalaryAdjustmentAddFormState;
  readonly isAmountValid: boolean;
  readonly isSubmitting: boolean;
  readonly options: DropdownOption[];
  readonly onAdd: () => void;
  readonly onAmountChange: (value: string) => void;
  readonly onSelectedTypeChange: (key: string) => void;
}

export const AdjustSalaryModalForm = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const isTypeUnavailable = props.addFormState.type === "unavailable";
  const presentation =
    props.addFormState.type === "unavailable"
      ? undefined
      : salaryAdjustmentPresentation[props.addFormState.key];
  const amount =
    props.addFormState.type === "available" ? props.addFormState.amount : "";

  return (
    <AdjustmentForm>
      <Dropdown
        disabled={props.isSubmitting || props.options.length === 0}
        fullWidth
        label={t("Salary.AdjustmentType")}
        options={props.options}
        placeholder={t("Salary.SelectAdjustment")}
        value={
          props.addFormState.type === "available"
            ? String(props.addFormState.key)
            : undefined
        }
        onChange={props.onSelectedTypeChange}
      />
      <InputField
        disabled={props.isSubmitting || isTypeUnavailable}
        errorText={t("Salary.AdjustmentAmountInvalid")}
        fullWidth
        hasError={amount.length > 0 && !props.isAmountValid}
        icon={
          presentation?.fixedSign ? (
            <AmountSign>{presentation.fixedSign}</AmountSign>
          ) : isTypeUnavailable ? (
            <span aria-hidden="true" />
          ) : undefined
        }
        iconPosition={presentation?.fixedSign ? "left" : "right"}
        label={t("Salary.AdjustmentAmount")}
        maxLength={presentation?.inputMaxLength}
        placeholder={
          presentation
            ? t(presentation.placeholderTranslationKey)
            : t("Salary.AdjustmentUnsignedAmountPlaceholder")
        }
        type="text"
        value={amount}
        onChange={props.onAmountChange}
      />
      <PrimaryButton
        aria-label={t("Salary.AddAdjustment")}
        disabled={
          isTypeUnavailable || !props.isAmountValid || props.isSubmitting
        }
        iconName="v2/plus"
        label={t("Salary.AddAdjustment")}
        size="small"
        onClick={props.onAdd}
      />
    </AdjustmentForm>
  );
};
